import React, { useCallback, useEffect, useState } from "react";
import i18n from "utils/i18n";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import { useShowHamburger } from 'utils/helper-device'
import _ from 'lodash'
import { useNavigation, useRoute } from '@react-navigation/native'
import { createTranslationField, retrieveTranslationFields } from 'components/molecules/Form'
import { FormStatus } from 'utils/constants/form-constants';
import LoadingComponent from 'components/atoms/Loading'
import GraphqlBuilder, { graphqlOptions } from 'utils/request/graphql/builder'
import MutateTranslation from 'utils/mutate/translation'
import { constantsActive, tableConstants } from 'utils/constants/constants'
import { AppRoutes } from 'navigations/app-routes'
import MutationManagementTimeslotGroup from 'graphql/mutations/management/MutationManagementTimeslotGroup'
import HelperString from 'utils/helper-string'
import TabbedContent from 'components/molecules/TabbedContent'
import Translate from 'utils/translate'
import { useSelector } from 'react-redux'
import { dataConstants } from 'services/redux/data/constants'
import ManagementTimeslotGroupBasicTab from 'scenes/management/timeslots/timeslot_group/_tabs/Basic'
import FetchTimeslotGroups from 'utils/fetch/FetchTimeslotGroups'
import ManagementTimeslotGroupAdvancedTab from 'scenes/management/timeslots/timeslot_group/_tabs/Advanced'
import ManagementTimeslotGroupTimeslotKindsTab from 'scenes/management/timeslots/timeslot_group/_tabs/TimeslotKinds'
import FetchCachedTimeslotGroups from 'utils/fetch/cached/FetchCachedTimeslotGroups'

const ManagementTimeslotGroupScene = () => {
	const navigation = useNavigation();
	const showHamburger = useShowHamburger();
	const route = useRoute();
	
	const _languages = useSelector( state => {
		return state.data[dataConstants.LANGUAGES];
	} )
	
	const [ loading, setLoading ] = useState( true );
	const [ saving, setSaving ] = useState( false );
	
	const [ timeslotGroup, setTimeslotGroup ] = useState( null );
	
	const timeslotGroupGuid = _.get( route.params, 'timeslotGroupGuid', null );
	
	const fetchedTimeslotGroup = ( timeslotGroup ) => {
		if( _.size( _languages ) > 0 ) {
			// Add the translation fields
			_.forEach( _languages, ( language ) => {
				let { name } = createTranslationField( 'name', language );
				const translation = Translate.formField( timeslotGroup, 'title', language );
				if( !_.isEmpty( timeslotGroup ) && translation !== null ) {
					timeslotGroup[ name ] = translation;
				}
				
				const { name: descriptionName } = createTranslationField( 'description', language );
				const descriptionTranslation = Translate.formField( timeslotGroup, 'description', language );
				if( !_.isEmpty( timeslotGroup ) && descriptionTranslation !== null ) {
					timeslotGroup[ descriptionName ] = descriptionTranslation;
				}
			});
		}
		
		_.forEach( _.get( timeslotGroup, 'timeslots_kinds'), (timeslotKind) => {
			timeslotGroup[ `[timeslotKind|${timeslotKind.guid}]`] = constantsActive.YES;
		});
		
		setTimeslotGroup( timeslotGroup );
		setSaving( false );
		setLoading( false );
	}
	
	const fetchTimeslotGroup = () => {
		FetchTimeslotGroups.fetch( {
			guid: timeslotGroupGuid,
			callbackStart: setLoading.bind( this, true ),
			callbackError: setLoading.bind( this, false ),
			callbackSuccess: fetchedTimeslotGroup
		});
	}
	
	const updateTimeslotGroup = ( values ) => {
		const timeslotKinds = [];
		_.forEach( values, ( value, key ) => {
			if( _.includes( key, '[timeslotKind' ) && value === constantsActive.YES){
				timeslotKinds.push( { guid: key.split( '|' )[ 1 ].replace(']', '' ) } );
			}
		});
		
		fetchedTimeslotGroup( {
			...values,
			timeslots_kinds: timeslotKinds,
			translations: retrieveTranslationFields( values )
		});
	}
	
	const updateTimeslotGroupDebounced = useCallback( _.debounce( updateTimeslotGroup, 500), []);
	
	const saveForm = ( values, { callback } = {} ) => {
		let timeslotKinds = [];
		_.forEach( values, ( value, key ) => {
			if( _.includes( key, '[timeslotKind' ) && value === constantsActive.YES){
				timeslotKinds.push( key.split( '|' )[ 1 ].replace(']', '' ) );
			}
		});
		
		values[ 'timeslot_kinds' ] = timeslotKinds;
		
		new GraphqlBuilder( { [ graphqlOptions.processVariables ]: false })
			.addQuery( 'mutation', MutationManagementTimeslotGroup, values )
			.setResultPath( ['mutation', 'timeslot_group'], {} )
			.setOnStart( () => {
				setSaving( true );
				callback( FormStatus.LOADING )
			})
			.setOnSuccess( ( result ) => {
				MutateTranslation.mutate( tableConstants.TIMESLOTGROUPS, result.guid, values, {
					callbackSuccess: ( t ) => {
						callback( FormStatus.SUCCESS );
						FetchCachedTimeslotGroups.reload();
						
						if( _.get( timeslotGroup, 'guid' ) !== result.guid ){
							navigation.replace( AppRoutes.MANAGEMENT_TIMESLOT_GROUPS, {
								timeslotGroupGuid: result.guid,
							});
						}else{
							fetchTimeslotGroup();
						}
					},
					callbackError: () => {
						callback( FormStatus.ERROR )
					}
				} )
			})
			.setOnError( ( result ) => {
				setSaving( false );
				callback( FormStatus.ERROR )
			})
			.execute()
	}
	
	useEffect( () => {
		if( timeslotGroupGuid !== null ){
			fetchTimeslotGroup();
		}else{
			fetchedTimeslotGroup({
				active: 'yes',
				can_book_for_self: 'yes',
				can_book_for_coworkers: 'yes',
				can_book_for_external: 'yes',
				can_only_book_single_timeslot_per_day: 'no',
				position: 999999
			});
		}
	}, [] );
	
	const tabBarItems = [];
	
	tabBarItems.push({
		key: 'timeslot_group_basic',
		text: i18n.t('management:form_block.title_default'),
		scene: ( props ) => {
			return (
				<ManagementTimeslotGroupBasicTab
					saveForm={ saveForm }
					updateTimeslotGroup={ updateTimeslotGroupDebounced }
					timeslotGroup={ timeslotGroup }
					isSaving={ saving }
				/>
			);
		}
	});
	tabBarItems.push({
		key: 'timeslot_group_timeslot_kinds',
		text: i18n.t( 'management:pages.timeslots.timeslotKinds.title'),
		scene: ( props ) => {
			return (
				<ManagementTimeslotGroupTimeslotKindsTab
					saveForm={ saveForm }
					updateTimeslotGroup={ updateTimeslotGroupDebounced }
					timeslotGroup={ timeslotGroup }
					isSaving={ saving }
				/>
			);
		}
	});
	tabBarItems.push({
		key: 'timeslot_group_advanced',
		text: i18n.t('management:form_block.title_advanced'),
		scene: ( props ) => {
			return (
				<ManagementTimeslotGroupAdvancedTab
					saveForm={ saveForm }
					updateTimeslotGroup={ updateTimeslotGroupDebounced }
					timeslotGroup={ timeslotGroup }
					isSaving={ saving }
				/>
			);
		}
	});
	
	let content = <LoadingComponent />
	if( !loading ) {
		content = (
			<TabbedContent
				key={ HelperString.hash('profile-tabs' ) }
				items={ tabBarItems }
			/>
		)
	}
	
	return (
		<ThemedSafeAreaComponentTop
			top={ false }>
			
			<ThemedLayout
				title={ i18n.t( 'management:pages.timeslots.timeslotGroups.title') }
				topNavigation={true}
				topNavigationProps={{
					showHamburgerDrawer: showHamburger
				}}
				showBackButton={ true }>
				
				{ content }
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	);
}

export default ManagementTimeslotGroupScene
